import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';

interface DiaCalendario {
  dia: number;
  mes: number;
  anio: number;
  esHoy: boolean;
  esMesActual: boolean;
  tieneEfemeride: boolean;
  fecha: Date;
}

@Component({
  selector: 'app-calendario-efemerides',
  templateUrl: './calendario-efemerides.component.html',
  styleUrls: ['./calendario-efemerides.component.scss']
})
export class CalendarioEfemeridesComponent implements OnInit {

  @Input() fechaInicial: { dia: number, mes: number } | null = null;
  @Output() fechaSeleccionada = new EventEmitter<{ dia: number, mes: number }>();

  mesActual: number;
  anioActual: number;
  diasCalendario: DiaCalendario[] = [];
  nombreMes: string;
  diasSemana: string[] = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  meses: string[] = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  diaSeleccionado: number | null = null;
  mesSeleccionado: number | null = null;

  constructor(private cdr: ChangeDetectorRef) {
    const hoy = new Date();
    this.mesActual = hoy.getMonth();
    this.anioActual = hoy.getFullYear();
    this.nombreMes = this.meses[this.mesActual];
  }

  ngOnInit(): void {
    // Si hay una fecha previamente seleccionada, restaurar el estado
    if (this.fechaInicial) {
      // fechaInicial.mes viene en formato 1-12, convertir a 0-11
      const mesIndex = this.fechaInicial.mes - 1;
      this.mesActual = mesIndex;
      this.diaSeleccionado = this.fechaInicial.dia;
      this.mesSeleccionado = mesIndex;
    }
    this.generarCalendario();
  }

  /**
   * Genera el calendario del mes actual
   */
  generarCalendario(): void {
    this.diasCalendario = [];
    const hoy = new Date();
    const primerDia = new Date(this.anioActual, this.mesActual, 1);
    const ultimoDia = new Date(this.anioActual, this.mesActual + 1, 0);

    // Día de la semana del primer día (0 = Domingo)
    const diaSemanaInicio = primerDia.getDay();

    // Días del mes anterior para completar la primera semana
    const ultimoDiaMesAnterior = new Date(this.anioActual, this.mesActual, 0).getDate();
    for (let i = diaSemanaInicio - 1; i >= 0; i--) {
      const dia = ultimoDiaMesAnterior - i;
      const fecha = new Date(this.anioActual, this.mesActual - 1, dia);
      this.diasCalendario.push({
        dia: dia,
        mes: this.mesActual === 0 ? 11 : this.mesActual - 1,
        anio: this.mesActual === 0 ? this.anioActual - 1 : this.anioActual,
        esHoy: false,
        esMesActual: false,
        tieneEfemeride: false,
        fecha: fecha
      });
    }

    // Días del mes actual
    for (let dia = 1; dia <= ultimoDia.getDate(); dia++) {
      const fecha = new Date(this.anioActual, this.mesActual, dia);
      const esHoy = hoy.getDate() === dia &&
                    hoy.getMonth() === this.mesActual &&
                    hoy.getFullYear() === this.anioActual;

      this.diasCalendario.push({
        dia: dia,
        mes: this.mesActual,
        anio: this.anioActual,
        esHoy: esHoy,
        esMesActual: true,
        tieneEfemeride: false,
        fecha: fecha
      });
    }

    // Días del mes siguiente para completar la última semana
    const diasRestantes = 42 - this.diasCalendario.length; // 6 semanas = 42 días
    for (let dia = 1; dia <= diasRestantes; dia++) {
      const fecha = new Date(this.anioActual, this.mesActual + 1, dia);
      this.diasCalendario.push({
        dia: dia,
        mes: this.mesActual === 11 ? 0 : this.mesActual + 1,
        anio: this.mesActual === 11 ? this.anioActual + 1 : this.anioActual,
        esHoy: false,
        esMesActual: false,
        tieneEfemeride: false,
        fecha: fecha
      });
    }

    this.nombreMes = this.meses[this.mesActual];
  }

  /**
   * Navegar al mes anterior
   */
  mesAnterior(): void {
    if (this.mesActual === 0) {
      this.mesActual = 11;
      this.anioActual--;
    } else {
      this.mesActual--;
    }
    this.generarCalendario();
  }

  /**
   * Navegar al mes siguiente
   */
  mesSiguiente(): void {
    if (this.mesActual === 11) {
      this.mesActual = 0;
      this.anioActual++;
    } else {
      this.mesActual++;
    }
    this.generarCalendario();
  }

  /**
   * Ir al mes actual (hoy)
   */
irHoy(): void {
  const hoy = new Date();
  this.mesActual = hoy.getMonth();
  this.anioActual = hoy.getFullYear();
  this.generarCalendario();

  // Buscar el objeto DiaCalendario correspondiente al día de hoy
  const diaHoy = this.diasCalendario.find(d =>
    d.dia === hoy.getDate() &&
    d.mes === hoy.getMonth() &&
    d.anio === hoy.getFullYear()
  );

  if (diaHoy) {
    // Marcarlo como seleccionado
    this.diaSeleccionado = diaHoy.dia;
    this.mesSeleccionado = diaHoy.mes;

    // console.log('📅 Navegando a HOY:', {
      // diaSeleccionado: this.diaSeleccionado,
      // mesSeleccionado: this.mesSeleccionado
    // });

    // Forzar detección de cambios
    this.cdr.detectChanges();

    // Emitir el evento completo (puedes incluir 'anio' si lo necesitas)
    this.fechaSeleccionada.emit({
      dia: diaHoy.dia,
      mes: diaHoy.mes + 1, // mes en formato 1-12
      // anio: diaHoy.anio // <- opcional, si deseas incluir el año
    });
  }
}


  /**
   * Seleccionar un día
   */
  seleccionarDia(diaCalendario: DiaCalendario): void {
    if (!diaCalendario.esMesActual) {
      // console.log('⚠️ Día de otro mes, no se puede seleccionar:', diaCalendario.dia);
      return; // No permitir seleccionar días de otros meses
    }

    // console.log('📅 Seleccionando día:', {
      // dia: diaCalendario.dia,
      // mes: diaCalendario.mes,
      // mesActual: this.mesActual,
      // esHoy: diaCalendario.esHoy
    // });

    this.diaSeleccionado = diaCalendario.dia;
    this.mesSeleccionado = diaCalendario.mes;

    // console.log('📌 Estado después de selección:', {
      // diaSeleccionado: this.diaSeleccionado,
      // mesSeleccionado: this.mesSeleccionado,
      // 'Debe aplicarse .seleccionado a': `día ${this.diaSeleccionado} del mes ${this.mesSeleccionado}`
    // });

    // Forzar detección de cambios para actualizar la vista
    this.cdr.detectChanges();

    // console.log('🎨 Detección de cambios ejecutada - la vista debería actualizarse');

    // Verificar si la clase se aplicó en el DOM
    setTimeout(() => {
      const elementosSeleccionados = document.querySelectorAll('.dia-cell.seleccionado');
      // console.log('🔎 Elementos con clase .seleccionado en el DOM:', elementosSeleccionados.length);

      if (elementosSeleccionados.length === 0) {
        console.error('❌ ¡NO SE ENCONTRÓ NINGÚN ELEMENTO CON .seleccionado!');
        // console.log('🔍 Buscando el día manualmente...');
        const todosLosDias = document.querySelectorAll('.dia-cell');
        todosLosDias.forEach((el: any) => {
          if (el.getAttribute('data-dia') === String(this.diaSeleccionado) &&
              el.getAttribute('data-mes') === String(this.mesSeleccionado)) {
            // console.log('📍 Encontrado el elemento del día seleccionado:', el);
            // console.log('📋 Clases del elemento:', el.className);
          }
        });
      } else {
        elementosSeleccionados.forEach((el: any) => {
          // console.log('✅ Elemento seleccionado encontrado:', {
            // dia: el.getAttribute('data-dia'),
            // mes: el.getAttribute('data-mes'),
            // clases: el.className
          // });
        });
      }
    }, 100);


    // Emitir evento con la fecha seleccionada (mes en formato 1-12)
    this.fechaSeleccionada.emit({
      dia: diaCalendario.dia,
      mes: diaCalendario.mes + 1
    });
  }


}
